import { getStandardRetryDelay, getStandardRetryLogic } from "#lib/errors"
import { queryKeys as accountQueryKeys } from "#lib/query-keys"
import { showToast } from "#lib/toast"
import { triggerCurrentAccountChange } from "#sdk/account-callback"
import { homeFinancialSDK } from "#sdk/sdk"
import type { CreateInvitation } from "@home-finance-manager/shared"
import type { Account } from "@home-finance-manager/shared/types/accounts"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

const queryKeys = {
  all: ["invitations"] as const,
  lists: () => [...queryKeys.all, "list"] as const,
  list: (status?: string) => [...queryKeys.lists(), status] as const,
  details: () => [...queryKeys.all, "detail"] as const,
  detail: (id: number) => [...queryKeys.details(), id] as const,
}

/**
 * Hook to fetch all invitations for user's owned accounts
 */
export function useInvitations(status?: string) {
  return useQuery({
    queryKey: queryKeys.list(status),
    queryFn: async () => {
      const response = await homeFinancialSDK.invitations.getInvitations(status)
      return response.data
    },
  })
}

/**
 * Hook to create invitation(s)
 */
export function useCreateInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateInvitation) => {
      return await homeFinancialSDK.invitations.createInvitation(data)
    },
    onSuccess: () => {
      // Invalidate all invitation lists
      queryClient.invalidateQueries({ queryKey: queryKeys.lists() })
    },
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

/**
 * Hook to cancel an invitation
 */
export function useCancelInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (invitationId: number) => {
      return await homeFinancialSDK.invitations.cancelInvitation(invitationId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lists() })
      showToast.success("Invitation cancelled successfully")
    },
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

/**
 * Hook to resend an invitation
 */
export function useResendInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (invitationId: number) => {
      return await homeFinancialSDK.invitations.resendInvitation(invitationId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lists() })
      showToast.success("Invitation resent successfully")
    },
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

/**
 * Hook to accept an invitation (for existing users)
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (token: string) => {
      return await homeFinancialSDK.invitations.acceptInvitation(token)
    },
    onSuccess: () => {
      // Invalidate accounts and invitations (non-blocking)
      queryClient.invalidateQueries({ queryKey: accountQueryKeys.accounts.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.lists() })
      // Set current account in the background after a short delay to allow refetch
      setTimeout(() => {
        const updatedAccounts = queryClient.getQueryData<Account[]>(
          accountQueryKeys.accounts.lists()
        )
        // If we have accounts but no current account set, set the first one
        if (updatedAccounts && updatedAccounts.length > 0) {
          const currentAccount = queryClient.getQueryData<Account | null>(
            accountQueryKeys.accounts.current()
          )
          if (!currentAccount) {
            // Set the first account (or default if available) as current
            const defaultAccount =
              updatedAccounts.find((acc) => acc.meta?.pivot_is_default) || updatedAccounts[0]
            if (defaultAccount) {
              localStorage.setItem("current_account", JSON.stringify(defaultAccount))
              triggerCurrentAccountChange(defaultAccount)
              queryClient.setQueryData(accountQueryKeys.accounts.current(), defaultAccount)
            }
          }
        }
      }, 100)
      showToast.success("Invitation accepted successfully")
    },
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}
